<div class="relative w-full py-6 [padding-inline:var(--shell-pad-x)] lg:py-8">
  <section appUiPanelCard class="surface-noise mb-6 overflow-hidden p-5 lg:p-7">
    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-end">
      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-accent">Design system</p>
        <h1 class="mt-3 max-w-4xl font-brand text-3xl font-bold leading-tight text-ink sm:text-5xl">
          Automato interface system
        </h1>
        <p class="mt-4 max-w-3xl text-sm leading-6 text-muted sm:text-base">
          Linguagem visual para um sistema operacional AI-first: precisa, premium, acessivel e pronta para workflows,
          objetos de negocio, agentes e integracoes.
        </p>
      </div>

      <aside appUiPanelCard class="surface-noise" aria-label="Resumo do sistema visual">
        <div class="flex items-center justify-between gap-3">
          <div>
            <p appUiLabel class="uppercase tracking-wide">Base atual</p>
            <p class="mt-1 text-lg font-semibold text-ink">Headless primitives</p>
          </div>
          <span appBadge tone="accent" size="xs">v0</span>
        </div>

        <div class="mt-5 grid gap-2 text-sm text-muted">
          <div appUiRecordRow [interactive]="false" class="min-h-0 px-3 py-2">
            <span>Angular</span>
            <span class="font-semibold text-ink">21</span>
          </div>
          <div appUiRecordRow [interactive]="false" class="min-h-0 px-3 py-2">
            <span>Primitives</span>
            <span class="font-semibold text-accent-strong">ng-primitives</span>
          </div>
          <div appUiRecordRow [interactive]="false" class="min-h-0 px-3 py-2">
            <span>Material</span>
            <span class="font-semibold text-tomato">granular</span>
          </div>
        </div>
      </aside>
    </div>
  </section>

  <section class="mb-6 grid gap-4 md:grid-cols-3" aria-labelledby="principles-title">
    <div class="md:col-span-3">
      <p class="text-xs font-semibold uppercase tracking-wide text-accent">Principios</p>
      <h2 id="principles-title" class="mt-1 font-brand text-2xl font-bold text-ink">Como a UI deve se sentir</h2>
    </div>

    @for (principle of principles; track principle.title) {
      <article appUiPanelCard class="surface-noise">
        <h3 appUiPanelTitle>{{ principle.title }}</h3>
        <p appUiPanelText>{{ principle.description }}</p>
      </article>
    }
  </section>

  <section class="mb-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_21rem]" aria-labelledby="foundations-title">
    <div appUiPanelCard class="p-5">
      <div class="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-accent">Foundations</p>
          <h2 id="foundations-title" class="mt-1 font-brand text-2xl font-bold text-ink">Tokens essenciais</h2>
        </div>
        <p class="max-w-md text-sm leading-6 text-muted">A paleta e quase neutra; emerald conduz acao, tomato sinaliza atencao.</p>
      </div>

      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        @for (token of colorTokens; track token.name) {
          <article appUiPanelCard class="overflow-hidden p-0">
            <div class="h-20 {{ token.className }}"></div>
            <div class="p-3">
              <p class="text-sm font-semibold text-ink">{{ token.name }}</p>
              <p appUiHint class="mt-1 font-mono">{{ token.value }}</p>
            </div>
          </article>
        }
      </div>
    </div>

    <aside appUiPanelCard class="bg-ink text-panel" aria-label="Tipografia">
      <p class="text-xs font-semibold uppercase tracking-wide text-accent-soft">Typography</p>
      <p class="mt-4 font-brand text-4xl font-bold leading-none">Figtree</p>
      <p class="mt-3 text-sm leading-6 text-panel/70">
        Logo e headings usam Figtree. Interface e corpo usam Plus Jakarta Sans para leitura densa e precisa.
      </p>
      <div appUiPanelCard class="mt-5 bg-dark-panel">
        <p class="text-xs uppercase tracking-wide text-panel/60">Scale</p>
        <p class="mt-2 text-2xl font-semibold">Aa 32</p>
        <p class="mt-1 text-sm text-panel/70">Aa 14, operational copy</p>
      </div>
    </aside>
  </section>

  <section appUiPanelCard class="workflow-rail mb-6" aria-label="Workflow pattern">
    <div class="grid gap-3 md:grid-cols-3">
      @for (step of workflowSteps; track step.number) {
        <article appUiPanelCard class="workflow-step">
          <p class="text-xs font-semibold uppercase tracking-wide text-accent">{{ step.number }}</p>
          <h2 class="mt-2 text-sm font-semibold text-ink">{{ step.title }}</h2>
          <p class="mt-1 text-sm leading-5 text-muted">{{ step.description }}</p>
        </article>
      }
    </div>
  </section>

  <section appUiPanelCard class="mb-6 p-5" aria-labelledby="components-title">
    <div class="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-accent">Components</p>
        <h2 id="components-title" class="mt-1 font-brand text-2xl font-bold text-ink">Primitives com estilo Automato</h2>
      </div>
      <p class="max-w-xl text-sm leading-6 text-muted">
        Wrappers finos preservam a acessibilidade dos primitives e concentram o visual no design system.
      </p>
    </div>

    <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div class="grid gap-4 md:grid-cols-2">
        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Button</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Acoes e comandos</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpButton</span>
          </div>
          <div class="mt-5 flex flex-wrap gap-2">
            <button appButton variant="primary" type="button">
              <ng-icon name="heroBolt" size="16" />
              Criar workflow
            </button>
            <button appButton variant="secondary" type="button">
              <ng-icon name="heroSparkles" size="16" />
              Revisar contexto
            </button>
            <button appButton variant="ghost" type="button">Cancelar</button>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Dialog</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Fluxo focado</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpDialog</span>
          </div>
          <div class="mt-5">
            <button appButton variant="primary" type="button" [ngpDialogTrigger]="dialogContent">
              <ng-icon name="heroSparkles" size="16" />
              Abrir dialog
            </button>
            <ng-template #dialogContent let-close="close">
              <div appDialogOverlay>
                <section appDialog>
                  <h2 appDialogTitle>Criar regra de handoff</h2>
                  <p appDialogDescription>
                    Use dialogs para tarefas focadas que precisam de confirmacao, contexto e retorno claro ao fluxo.
                  </p>
                  <div class="mt-5 grid gap-3">
                    <label appUiLabel class="grid gap-1.5 uppercase tracking-wide" for="dialog-rule-name">
                      Nome da regra
                      <input appInput surface="transparent" id="dialog-rule-name" value="Lead com urgencia alta" />
                    </label>
                    <label appUiLabel class="grid gap-1.5 uppercase tracking-wide" for="dialog-rule-note">
                      Instrucao
                      <textarea appTextarea surface="transparent" id="dialog-rule-note">Transferir para CS quando o lead mencionar contrato ativo.</textarea>
                    </label>
                  </div>
                  <div class="mt-5 flex flex-wrap justify-end gap-2">
                    <button appButton variant="ghost" type="button" (click)="close()">Cancelar</button>
                    <button appButton variant="primary" type="button" (click)="close()">
                      <ng-icon name="heroCheck" size="16" />
                      Salvar regra
                    </button>
                  </div>
                </section>
              </div>
            </ng-template>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Select</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Escolha simples</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpSelect</span>
          </div>
          <div class="mt-5 grid gap-1.5">
            <label appUiLabel class="uppercase tracking-wide" for="pipeline-select">
              Pipeline
            </label>
            <div
              appSelect
              id="pipeline-select"
              [ngpSelectValue]="selectedPipeline()"
              (ngpSelectValueChange)="setSelectedPipeline($event)"
            >
            <span>{{ selectedPipeline() }}</span>
            <div
              *ngpSelectPortal
              appSelectDropdown
            >
              <div appSelectOption ngpSelectOptionValue="Inbound enterprise">
                Inbound enterprise
                @if (selectedPipeline() === 'Inbound enterprise') {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
              <div appSelectOption ngpSelectOptionValue="Expansao CS">
                Expansao CS
                @if (selectedPipeline() === 'Expansao CS') {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
              <div appSelectOption ngpSelectOptionValue="Revenda e parceiros">
                Revenda e parceiros
                @if (selectedPipeline() === 'Revenda e parceiros') {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
            </div>
          </div>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Multiselect</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Segmentos ativos</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpSelect</span>
          </div>
          <div class="mt-5 grid gap-1.5">
            <label appUiLabel class="uppercase tracking-wide" for="segment-select">
              Segmentos
            </label>
            <div
              appSelect
              id="segment-select"
              [ngpSelectMultiple]="true"
              [ngpSelectValue]="selectedSegments()"
              (ngpSelectValueChange)="setSelectedSegments($event)"
            >
            <span>{{ selectedSegmentsLabel() }}</span>
            <div
              *ngpSelectPortal
              appSelectDropdown
            >
              <div appSelectOption ngpSelectOptionValue="Leads inbound">
                Leads inbound
                @if (selectedSegments().includes('Leads inbound')) {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
              <div appSelectOption ngpSelectOptionValue="Clientes ativos">
                Clientes ativos
                @if (selectedSegments().includes('Clientes ativos')) {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
              <div appSelectOption ngpSelectOptionValue="Risco de churn">
                Risco de churn
                @if (selectedSegments().includes('Risco de churn')) {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
              <div appSelectOption ngpSelectOptionValue="Trial enterprise">
                Trial enterprise
                @if (selectedSegments().includes('Trial enterprise')) {
                  <ng-icon name="heroCheck" size="16" />
                }
              </div>
            </div>
          </div>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Textarea</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Contexto longo</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpTextarea</span>
          </div>
          <div class="mt-5 grid gap-1.5">
            <label appUiLabel class="uppercase tracking-wide" for="agent-context">
              Instrucao do agente
            </label>
            <textarea appTextarea id="agent-context">Responda com base no historico do CRM, respeitando o SLA do business e sinalizando quando precisar de handoff humano.</textarea>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Toggle group</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Controles segmentados</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpToggleGroup</span>
          </div>
          <div class="mt-5 grid gap-3">
            <div
              layout="segmented"
              appToggleGroup
              ngpToggleGroupType="single"
              [ngpToggleGroupValue]="selectedMode()"
              (ngpToggleGroupValueChange)="setSelectedMode($event)"
            >
              <span
                appToggleIndicator
                [class.translate-x-0]="selectedMode()[0] === 'compact'"
                [class.translate-x-full]="selectedMode()[0] === 'operational'"
                [class.translate-x-[200%]]="selectedMode()[0] === 'analysis'"
                aria-hidden="true"
              ></span>
              <button appToggleGroupItem [indicator]="true" ngpToggleGroupItemValue="compact">Compacto</button>
              <button appToggleGroupItem [indicator]="true" ngpToggleGroupItemValue="operational">Operacional</button>
              <button appToggleGroupItem [indicator]="true" ngpToggleGroupItemValue="analysis">Analise</button>
            </div>
            <div
              appToggleGroup
              ngpToggleGroupType="multiple"
              [ngpToggleGroupValue]="selectedAreas()"
              (ngpToggleGroupValueChange)="setSelectedAreas($event)"
            >
              <button appToggleGroupItem tone="accent" ngpToggleGroupItemValue="crm">CRM</button>
              <button appToggleGroupItem tone="accent" ngpToggleGroupItemValue="agents">Agentes</button>
              <button appToggleGroupItem tone="accent" ngpToggleGroupItemValue="billing">Billing</button>
            </div>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4 md:col-span-2">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Tabs</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Secoes de configuracao</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpTabs</span>
          </div>
          <div
            class="mt-5 grid gap-3"
            appTabs
            [ngpTabsetValue]="selectedTab()"
            (ngpTabsetValueChange)="setSelectedTab($event)"
          >
            <div appTabList>
              <span
                appTabIndicator
                [class.translate-x-0]="selectedTab() === 'tokens'"
                [class.translate-x-full]="selectedTab() === 'components'"
                [class.translate-x-[200%]]="selectedTab() === 'patterns'"
                aria-hidden="true"
              ></span>
              <button
                appTabButton
                ngpTabButtonValue="tokens"
              >
                Tokens
              </button>
              <button
                appTabButton
                ngpTabButtonValue="components"
              >
                Componentes
              </button>
              <button
                appTabButton
                ngpTabButtonValue="patterns"
              >
                Patterns
              </button>
            </div>
            <div
              appTabPanel
              ngpTabPanelValue="tokens"
            >
              Tokens definem cor, raio, sombra e material. Eles devem ser usados antes de qualquer estilo local.
            </div>
            <div
              appTabPanel
              ngpTabPanelValue="components"
            >
              Componentes encapsulam primitives headless e preservam estados acessiveis sem espalhar classes cruas.
            </div>
            <div
              appTabPanel
              ngpTabPanelValue="patterns"
            >
              Patterns combinam componentes em fluxos: contexto, objetos, decisoes e proxima acao.
            </div>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Menu</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Comandos contextuais</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpMenu</span>
          </div>
          <div class="mt-5">
            <button appButton type="button" [ngpMenuTrigger]="demoMenu" ngpMenuTriggerPlacement="bottom-start">
              <ng-icon name="heroCommandLine" size="16" />
              Abrir menu
              <ng-icon name="heroChevronDown" size="14" class="text-muted" />
            </button>
            <ng-template #demoMenu>
              <div appMenuPanel>
                <button appMenuItem type="button">
                  Abrir registro
                  <ng-icon name="heroChatBubbleLeftRight" size="16" class="text-muted" />
                </button>
                <button appMenuItem type="button">
                  Atribuir agente
                  <ng-icon name="heroSparkles" size="16" class="text-muted" />
                </button>
                <div appMenuSeparator></div>
                <button appMenuItem tone="danger" type="button">
                  Pausar automacao
                  <ng-icon name="heroBolt" size="16" />
                </button>
              </div>
            </ng-template>
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Input</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Busca e filtros</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpInput</span>
          </div>
          <div class="mt-5 grid gap-1.5">
            <label appUiLabel class="uppercase tracking-wide" for="system-search">
              Buscar contexto
            </label>
            <input appInput id="system-search" type="search" value="Conversas com leads ativos" />
          </div>
        </article>

        <article appUiPanelCard class="surface-noise p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-accent">Tooltip</p>
              <h3 class="mt-1 text-lg font-semibold text-ink">Ajuda contextual</h3>
            </div>
            <span appBadge tone="neutral" size="xs">NgpTooltip</span>
          </div>
          <div class="mt-5">
            <button
              appButton
              variant="secondary"
              type="button"
              [ngpTooltipTrigger]="tooltipContent"
              [ngpTooltipTriggerShowDelay]="0"
              [ngpTooltipTriggerHideDelay]="120"
            >
              <ng-icon name="heroAdjustmentsHorizontal" size="16" />
              Ver foco WCAG
            </button>
            <ng-template #tooltipContent>
              <div appTooltip>
                Form controls usam mudanca de borda e fundo no foco, sem outline.
              </div>
            </ng-template>
          </div>
        </article>
      </div>

      <aside appUiPanelCard class="surface-noise" aria-label="Inventario de componentes">
        <p appUiLabel class="uppercase tracking-wide">Inventory</p>
        <div class="mt-4 grid gap-3">
          @for (component of components; track component.name) {
            <article appUiPanelCard>
              <div class="flex items-center justify-between gap-3">
                <h3 appUiPanelTitle>{{ component.name }}</h3>
                <span appBadge tone="accent" size="xs">
                  {{ component.primitive }}
                </span>
              </div>
              <p appUiPanelText>{{ component.description }}</p>
            </article>
          }
        </div>
      </aside>
    </div>
  </section>

  <section class="grid gap-5 lg:grid-cols-2" aria-labelledby="patterns-title">
    <div appUiPanelCard class="p-5">
      <p class="text-xs font-semibold uppercase tracking-wide text-accent">Patterns</p>
      <h2 id="patterns-title" class="mt-1 font-brand text-2xl font-bold text-ink">Objetos operacionais</h2>
      <div class="mt-5 grid gap-3">
        <article appUiPanelCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-base font-semibold text-ink">Lead inbound qualificado</h3>
              <p class="mt-1 text-sm leading-5 text-muted">Origem: campanha AI landing page, status: aguardando handoff.</p>
            </div>
            <span appBadge tone="accent" size="sm">82%</span>
          </div>
        </article>
        <article appUiPanelCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-base font-semibold text-ink">Agente de triagem</h3>
              <p class="mt-1 text-sm leading-5 text-muted">Memória ativa, regras de transferência e base de conhecimento conectadas.</p>
            </div>
            <span appBadge tone="danger" size="xs">Aten&ccedil;&atilde;o</span>
          </div>
        </article>
      </div>
    </div>

    <div appUiPanelCard class="bg-ink text-panel">
      <p class="text-xs font-semibold uppercase tracking-wide text-accent-soft">Dark mode tokens</p>
      <h2 class="mt-1 font-brand text-2xl font-bold">Pronto para alternância</h2>
      <p class="mt-3 text-sm leading-6 text-panel/70">
        A base continua light-first, mas os tokens escuros já existem para painéis densos, command surfaces e estados
        que pedem menor luminância.
      </p>
      <div class="mt-5 grid grid-cols-2 gap-3">
        <div appUiPanelCard class="bg-dark-app">
          <p class="text-xs text-panel/60">dark-app</p>
          <p class="mt-2 font-mono text-xs">var(--color-dark-app)</p>
        </div>
        <div appUiPanelCard class="bg-dark-panel">
          <p class="text-xs text-panel/60">dark-panel</p>
          <p class="mt-2 font-mono text-xs">var(--color-dark-panel)</p>
        </div>
      </div>
    </div>
  </section>
</div>
